import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import PageHeader from '../../../components/common/ui/PageHeader';
import ServiceCard from '../../../components/common/ui/ServiceCard';
import {
  getAllFavorites,
  getFavoriteCount,
} from '../../../../service/favoriteService';
import { getServiceDataById } from '../../../../service/serviceService';
import { useAuth } from '../../../../context/AuthContext';

const CustomerFavorite = () => {
  const { isAuthenticated } = useAuth();

  // Fetch all favorites for current user
  const { data: favoritesData, isLoading: favoritesLoading } = useQuery({
    queryKey: ['GET-FAVORITES-LIST'],
    queryFn: getAllFavorites,
    enabled: !!isAuthenticated,
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 401 || error?.response?.status === 403)
        return false;
      return failureCount < 2;
    },
  });

  const favoriteItems: any[] = useMemo(() => {
    if (!favoritesData) return [];
    if (Array.isArray((favoritesData as any)?.favorites)) {
      return (favoritesData as any).favorites;
    }
    if (Array.isArray((favoritesData as any)?.data)) {
      return (favoritesData as any).data;
    }
    if (Array.isArray(favoritesData)) {
      return favoritesData as any[];
    }
    return [];
  }, [favoritesData]);

  // Fetch the service details for each favorite's serviceId
  const serviceQueries = useQueries({
    queries: favoriteItems.map((f: any) => ({
      queryKey: ['FAVORITE-SERVICE-DETAIL', String(f?.serviceId)],
      queryFn: () => getServiceDataById(String(f?.serviceId)),
      enabled: !!isAuthenticated && !!f?.serviceId,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    })),
  });

  const servicesWithMeta = useMemo(() => {
    return favoriteItems
      .map((favorite, index) => {
        const queryResult = serviceQueries[index];
        const serviceInfo = queryResult?.data?.serviceInfo ?? queryResult?.data;
        return {
          favorite,
          service: serviceInfo,
        };
      })
      .filter((entry) => Boolean(entry.service));
  }, [favoriteItems, serviceQueries]);

  // Favorite count
  const { data: favoriteCountData } = useQuery({
    queryKey: ['FAVORITE-COUNT'],
    queryFn: getFavoriteCount,
    enabled: !!isAuthenticated,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const favoriteCount = useMemo(() => {
    if (typeof favoriteCountData === 'number') {
      return favoriteCountData;
    }
    if (
      favoriteCountData &&
      typeof (favoriteCountData as any)?.count === 'number'
    ) {
      return (favoriteCountData as any).count;
    }
    if (
      favoriteCountData &&
      typeof (favoriteCountData as any)?.total === 'number'
    ) {
      return (favoriteCountData as any).total;
    }
    return servicesWithMeta.length;
  }, [favoriteCountData, servicesWithMeta]);

  if (!isAuthenticated) {
    return (
      <div>
        <PageHeader
          title="Favorites"
          description="Please log in to view and manage your favorite services."
        />
        <div className="text-sm text-gray-500">
          Sign in to start saving services to your favorites list.
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Favorites"
        description={`Your favorite services ${favoriteCount ? `(${favoriteCount})` : ''}.`}
      />

      {/* Cards */}
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {favoritesLoading && (
          <div className="text-sm text-gray-500">Loading favorites...</div>
        )}
        {!favoritesLoading && servicesWithMeta.length === 0 && (
          <div className="text-sm text-gray-500">
            You have no favorite services yet.
          </div>
        )}
        {servicesWithMeta.map(({ favorite, service }: any) => (
          <ServiceCard
            key={service?.serviceId ?? favorite?.serviceId}
            data={service}
            initialFavorite={Boolean(favorite)}
          />
        ))}
      </div>
    </div>
  );
};

export default CustomerFavorite;
